import { useState } from "react";
import { ApiError, type TelegramAccess, api } from "../lib/api";
import { dateTime } from "../lib/format";

/** The client's way into a service's private Telegram channel. */
export function TelegramJoin({ subscriptionId, access, sandbox, onChange }: { subscriptionId: string; access: TelegramAccess; sandbox: boolean; onChange: (a: TelegramAccess) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      onChange(await api<TelegramAccess>(`/v1/me/subscriptions/${subscriptionId}/telegram-invite`, { method: "POST" }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not make a new link.");
    } finally {
      setBusy(false);
    }
  }

  async function simulateJoin() {
    await api("/v1/sandbox/telegram/join", { body: { inviteLink: access.inviteLink } });
    onChange({ ...access, status: "JOINED", inviteLink: null, inviteExpiresAt: null });
  }

  switch (access.status) {
    case "JOINED":
      return <span className="tg tg-joined">In {access.channel} on Telegram</span>;
    case "REMOVED":
    case "NONE":
      return null;
    case "INVITE":
      return (
        <div className="tg">
          <a className="tg-btn" href={access.inviteLink!} target="_blank" rel="noopener noreferrer">
            Join {access.channel} on Telegram
          </a>
          <span className="small muted">
            Works once, for one Telegram account, until {dateTime(access.inviteExpiresAt!)}.{" "}
            {sandbox && (
              <button className="link" onClick={() => void simulateJoin()}>
                Pretend I joined (test mode)
              </button>
            )}
          </span>
        </div>
      );
    default:
      return (
        <div className="tg">
          <span className="small muted">{access.status === "EXPIRED" ? "Your Telegram link has expired." : "Your Telegram link is being prepared."}</span>{" "}
          <button className="link" onClick={() => void refresh()} disabled={busy}>
            {busy ? "Making a link…" : "Get a new Telegram link"}
          </button>
          {error && <span className="small" role="alert"> {error}</span>}
        </div>
      );
  }
}
